import '@fontsource-variable/jetbrains-mono';
import { redirectToCentralLogin } from '@mg/auth-client';
import '@mg/ui/styles/root-styles.css';

import { MGErrorScreen } from '@mg/ui/components/MGErrorScreen';
import { registerMuiLicense } from '@mg/ui/muiLicense';
import { theme } from '@mg/ui/styles/theme';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { SuperTokensWrapper } from 'supertokens-auth-react';
import { messageOf, setAuthFailureHandlers } from './api/client.js';
import { queries } from './api/queries.js';
import type { MeState } from './api/types.js';
import { ErrorScreen } from './components/ui/ErrorScreen.js';
import { Loading } from './components/ui/misc.js';
import { initAuth } from './lib/auth.js';
import { installEnglishForMGUI } from './lib/mgUiI18n.js';
import { routeTree } from './routeTree.gen.js';

registerMuiLicense();
installEnglishForMGUI();

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

/** Signed in, but the role went away while a page was open. */
const NO_ROLE: MeState = { ok: false, reason: 'no-role' };

setAuthFailureHandlers({
  // No session left: hand the browser to the central login, which returns here afterwards.
  unauthorized: () => redirectToCentralLogin(),
  // Signed in, but without the role. Rewriting the cached identity is what puts the screen
  // naming the missing role up; a redirect would bounce between two origins forever.
  forbidden: () => queryClient.setQueryData(queries.me().queryKey, NO_ROLE),
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // React Query owns staleness; the router should not keep a second copy of the answer.
  defaultPreloadStaleTime: 0,
  defaultPendingComponent: Loading,
  defaultErrorComponent: ErrorScreen,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = createRoot(document.getElementById('root') as HTMLElement);

/** The providers every render below shares. */
function Providers({ children }: { children: ReactNode }): ReactNode {
  return (
    <StrictMode>
      {/* The library's MGSharedProviders would add Datadog RUM and PostHog on top; this tool traces
          with OpenTelemetry and has neither. */}
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StrictMode>
  );
}

/**
 * Boot: read the configuration, wire up the auth SDK, then render.
 *
 * The one await before the first paint, and it is not avoidable. The SDK has to be
 * initialised before any request goes out, and it needs the auth origin — which only the
 * server knows, because one build serves staging and production. Baking it in with a build
 * variable would mean two builds and a way to get them mixed up.
 *
 * Written as a function rather than top-level await on purpose: Vite's default build target
 * is ES2020, where top-level await is not available, and the failure is a build error rather
 * than something a review would catch.
 */
async function boot(): Promise<void> {
  let signInAvailable = false;
  let failedStep = 'The API did not answer';
  try {
    const config = await queryClient.fetchQuery(queries.config());
    failedStep = 'Sign-in could not start';
    signInAvailable = initAuth(config);
  } catch (error) {
    // The API is down, the reverse proxy is misconfigured, or the auth config does not fit this
    // host. Saying so beats a blank page, which is what an unhandled rejection here would leave
    // behind. `ErrorScreen` is not an option: it is a route error component and reads the
    // router, which does not exist yet.
    root.render(
      <Providers>
        <MGErrorScreen
          title={failedStep}
          subtitle={messageOf(error)}
          resetLabel="Try again"
          resetError={() => window.location.reload()}
        />
      </Providers>,
    );
    return;
  }

  // Started here so it travels alongside the translation fetch rather than queueing behind it.
  void queryClient.prefetchQuery(queries.me());

  const app = (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );

  root.render(
    <Providers>
      {/* Session state for the SDK's hooks, and it renders nothing of its own — but it throws
          when SuperTokens was never initialised, which is exactly the local case where there
          is no auth service to sign in to. */}
      {signInAvailable ? <SuperTokensWrapper>{app}</SuperTokensWrapper> : app}
    </Providers>,
  );
}

void boot();
